import React, { useEffect, useMemo, useState } from "react";
import { Mail, Pencil, Loader2, RotateCcw, User, Shield } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, {
  AdminMobileCard,
  adminTableHeadClass,
  adminTableRowClass,
  adminClickableRowClass,
} from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_BADGE_OK,
  ADMIN_BADGE_WARN,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

function interpolatePreview(text, vars = {}) {
  return String(text ?? "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) =>
    String(vars[key] ?? "")
  );
}

export default function AdminEmails() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ subject: "", html: "" });
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/email-templates");
      if (res.ok) setTemplates(await res.json());
      else toast.error("Impossible de charger les modèles d'e-mails");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (template) => {
    setEditing(template);
    setForm({ subject: template.subject, html: template.html });
  };

  const previewSubject = useMemo(
    () => interpolatePreview(form.subject, editing?.samples),
    [form.subject, editing]
  );
  const previewInner = useMemo(
    () => interpolatePreview(form.html, editing?.samples),
    [form.html, editing]
  );
  const previewHtml = useMemo(() => {
    const wrapper = editing?.email_wrapper;
    if (wrapper && wrapper.includes("<!--EMAIL_BODY-->")) {
      return wrapper.replace("<!--EMAIL_BODY-->", previewInner);
    }
    return previewInner;
  }, [editing, previewInner]);

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      const res = await authFetch(`/api/admin/email-templates/${editing.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Enregistrement impossible");
        return;
      }
      const updated = await res.json();
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditing(updated);
      setForm({ subject: updated.subject, html: updated.html });
      toast.success("Modèle enregistré");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await authFetch(`/api/admin/email-templates/${resetTarget.id}/reset`, {
        method: "POST",
      });
      if (!res.ok) {
        toast.error("Impossible de rétablir le modèle");
        return;
      }
      const updated = await res.json();
      setTemplates((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      if (editing?.id === updated.id) {
        setEditing(updated);
        setForm({ subject: updated.subject, html: updated.html });
      }
      toast.success("Modèle renvoyé au texte d'origine");
      setResetTarget(null);
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        icon={Mail}
        title="Modèles d'e-mails"
        description="Tous les messages automatiques (parents et équipe). Logo, couleurs et pied de page viennent des Paramètres — tu édites seulement le texte."
      />

      {loading ? (
        <AdminLoading message="Chargement des modèles..." />
      ) : templates.length === 0 ? (
        <AdminEmptyState
          icon={Mail}
          title="Aucun modèle"
          description="Les e-mails automatiques n'ont pas encore été initialisés."
        />
      ) : (
        <AdminTable
          mobileCards={templates.map((template) => (
            <AdminMobileCard
              key={template.id}
              title={template.name}
              subtitle={template.trigger}
              onClick={() => openEdit(template)}
              badge={
                <Badge className={template.customized ? ADMIN_BADGE_WARN : ADMIN_BADGE_OK}>
                  {template.customized ? "Modifié" : "Défaut"}
                </Badge>
              }
              meta={[
                { label: "Destinataire", value: template.audience === "parent" ? "Parent" : "Équipe" },
                { label: "Sujet", value: template.subject },
              ]}
              actions={
                <Button variant="ghost" size="sm" onClick={() => openEdit(template)} aria-label="Modifier">
                  <Pencil className="h-4 w-4" />
                </Button>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Modèle</TableHead>
                <TableHead className={adminTableHeadClass}>Destinataire</TableHead>
                <TableHead className={adminTableHeadClass}>Déclencheur</TableHead>
                <TableHead className={adminTableHeadClass}>Sujet</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow
                  key={template.id}
                  className={adminClickableRowClass}
                  onClick={() => openEdit(template)}
                >
                  <TableCell className="text-slate-900 font-medium">
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#3a00e0]" />
                      {template.name}
                    </span>
                  </TableCell>
                  <TableCell className={ADMIN_LABEL}>
                    <span className="inline-flex items-center gap-1.5">
                      {template.audience === "parent" ? (
                        <User className="h-3.5 w-3.5 text-slate-400" />
                      ) : (
                        <Shield className="h-3.5 w-3.5 text-slate-400" />
                      )}
                      {template.audience === "parent" ? "Parent" : "Équipe"}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{template.trigger}</TableCell>
                  <TableCell className="text-slate-500 text-sm max-w-xs truncate">
                    {template.subject}
                  </TableCell>
                  <TableCell>
                    <Badge className={template.customized ? ADMIN_BADGE_WARN : ADMIN_BADGE_OK}>
                      {template.customized ? "Modifié" : "Défaut"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(template)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className={`${ADMIN_PANEL} max-w-5xl max-h-[92vh] overflow-y-auto`}>
          {editing && (
            <form onSubmit={save} className="space-y-5">
              <DialogHeader>
                <DialogTitle className="text-slate-900">{editing.name}</DialogTitle>
                <p className="text-sm text-slate-500">{editing.description}</p>
              </DialogHeader>

              <div className="flex flex-wrap gap-1.5">
                {editing.variables.map((variable) => (
                  <button
                    key={variable.key}
                    type="button"
                    className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600 hover:border-[#3a00e0]/30 hover:text-[#3a00e0]"
                    onClick={() =>
                      navigator.clipboard
                        .writeText(`{{${variable.key}}}`)
                        .then(() => toast.success(`{{${variable.key}}} copié`))
                    }
                  >
                    {`{{${variable.key}}}`}
                    <span className="ml-1 text-slate-400">{variable.label}</span>
                  </button>
                ))}
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Sujet</Label>
                    <Input
                      value={form.subject}
                      onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                      className={ADMIN_FIELD}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Contenu HTML</Label>
                    <Textarea
                      value={form.html}
                      onChange={(e) => setForm((prev) => ({ ...prev, html: e.target.value }))}
                      rows={16}
                      className={`${ADMIN_FIELD} font-mono text-[13px] leading-relaxed`}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Aperçu (données fictives)
                  </p>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {previewSubject || "—"}
                  </div>
                  <iframe
                    title="Aperçu e-mail"
                    sandbox=""
                    srcDoc={previewHtml}
                    className="h-[28rem] w-full rounded-lg border border-slate-200 bg-slate-100"
                  />
                  <p className="text-xs text-slate-400">
                    Le logo et les coordonnées du pied de page sont ceux des Paramètres.
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-slate-500"
                  disabled={!editing.customized}
                  onClick={() => setResetTarget(editing)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Rétablir l'original
                </Button>
                <div className="flex gap-2">
                  <Button type="button" className={BTN_OUTLINE} onClick={() => setEditing(null)}>
                    Fermer
                  </Button>
                  <Button type="submit" disabled={saving} className={BTN_PRIMARY}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Enregistrement…
                      </>
                    ) : (
                      "Enregistrer"
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(resetTarget)}
        onOpenChange={(open) => !open && setResetTarget(null)}
        title="Rétablir le modèle d'origine ?"
        description="Les modifications de ce modèle seront perdues. Le texte initial sera renvoyé."
        confirmLabel="Rétablir"
        confirmingLabel="Rétablissement..."
        confirming={resetting}
        onConfirm={reset}
      />
    </div>
  );
}
